/**
 * PromControlPanel — /prom/:slug/control
 *
 * Teacher-facing remote control. One huge button to start the reveal,
 * plus failsafe "reveal now" + "reset" + "open live screen" actions.
 *
 * Designed to be used on a phone in front of a room — every action
 * is one tap, every label is unmistakable.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  PlayCircle, ArrowsClockwise, Crown, MonitorPlay, Copy, Sparkle, LockKey, Trophy, Megaphone, ChatCircleText, FilmReel,
} from '@phosphor-icons/react';
import { trackGrowth } from '../lib/growthTrack';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 2000;

export default function PromControlPanel() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const eventIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(ev.data);
        eventIdRef.current = ev.data.event_id;
      } catch (e) {
        if (!cancelled) setError('Could not load event.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Poll status every 2s
  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      if (!eventIdRef.current) {
        timer = setTimeout(tick, POLL_MS);
        return;
      }
      try {
        const res = await axios.get(`${API}/events/${eventIdRef.current}/reveal-status`);
        if (!cancelled) setStatus(res.data);
      } catch (e) { /* ignore */ }
      finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const startReveal = async () => {
    if (!confirming) {
      setConfirming(true);
      setTimeout(() => setConfirming(false), 4000);
      return;
    }
    setConfirming(false);
    try {
      await axios.post(`${API}/events/${eventIdRef.current}/start-reveal`, {});
      toast.success('🎬 Reveal started — open the live screen now');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start reveal');
    }
  };

  const revealNow = async () => {
    try {
      await axios.post(`${API}/events/${eventIdRef.current}/reveal-now`, {});
      toast.success('👑 Revealed instantly');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reveal');
    }
  };

  const resetReveal = async () => {
    try {
      await axios.post(`${API}/events/${eventIdRef.current}/reset-reveal`, {});
      toast.success('🔄 Reset to live voting');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset');
    }
  };

  const liveScreenUrl = typeof window !== 'undefined' ? `${window.location.origin}/prom/${slug}/live` : '';

  const copyLiveLink = async () => {
    try {
      await navigator.clipboard.writeText(liveScreenUrl);
      toast.success('Live screen URL copied — open it on your projector');
    } catch {
      toast.error('Could not copy — long-press the URL to copy manually');
    }
  };

  const openLive = () => {
    window.open(liveScreenUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center" data-testid="prom-control-loading">
        <Sparkle size={28} weight="fill" className="text-[#C29D52] animate-pulse" />
      </div>
    );
  }
  if (error || !event) {
    return <div className="min-h-screen bg-neutral-950 text-white p-6 text-center" data-testid="prom-control-error">{error || 'Not found'}</div>;
  }

  const phase = status?.phase || 'live';
  const phaseLabel = {
    live: { text: 'Voting live', icon: <Sparkle size={12} weight="fill" />, colour: 'text-emerald-300 bg-emerald-300/10 border-emerald-300/25' },
    locked: { text: 'Voting locked', icon: <LockKey size={12} weight="fill" />, colour: 'text-white/70 bg-white/8 border-white/15' },
    countdown: { text: `Countdown ${status?.seconds_remaining ?? 0}s`, icon: <Sparkle size={12} weight="fill" />, colour: 'text-[#C29D52] bg-[#B89762]/30/10 border-[#B89762]/40/25' },
    reveal: { text: 'Revealed', icon: <Crown size={12} weight="fill" />, colour: 'text-amber-300 bg-amber-300/10 border-amber-300/25' },
  }[phase];

  const canStart = phase === 'live';

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-5" data-testid="prom-control-panel">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/40">Host control</p>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.16em] border ${phaseLabel.colour}`} data-testid="prom-control-status-pill">
            {phaseLabel.icon} {phaseLabel.text}
          </span>
        </div>

        <h1 className="text-2xl font-black mb-1" data-testid="prom-control-title">{event.event_name}</h1>
        <p className="text-white/55 text-sm mb-7">{event.school_name || 'Prom'}</p>

        {/* PRIMARY BUTTON */}
        <button
          onClick={canStart ? startReveal : revealNow}
          className={`w-full inline-flex items-center justify-center gap-3 px-6 py-5 rounded-2xl text-base font-black text-white shadow-2xl mb-3 transition-transform active:scale-[0.98] ${
            canStart ? '' : 'opacity-90'
          }`}
          style={{ background: canStart
            ? 'linear-gradient(135deg, #B89762, #C29D52)'
            : 'linear-gradient(135deg, #f59e0b, #f97316)' }}
          data-testid="prom-control-primary-btn"
        >
          {canStart ? (
            confirming ? (
              <><PlayCircle size={22} weight="fill" /> Tap again to confirm</>
            ) : (
              <><PlayCircle size={22} weight="fill" /> Start Prom King Reveal</>
            )
          ) : (
            <><Crown size={22} weight="fill" /> Reveal now</>
          )}
        </button>
        <p className="text-[12px] text-white/45 text-center mb-6">
          {canStart
            ? (confirming ? 'You have ~4s to confirm' : 'Locks voting & starts the 30-second countdown')
            : 'Skip ahead to the winner screen'}
        </p>

        {/* COUNTDOWN PREVIEW (during countdown only) */}
        {phase === 'countdown' && (
          <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 text-center mb-6" data-testid="prom-control-countdown">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-white/55 mb-2">Countdown</p>
            <p className="text-5xl font-black tabular-nums">{String(Math.max(0, status?.seconds_remaining ?? 0)).padStart(2, '0')}</p>
            <p className="text-[12px] text-white/45 mt-2">Live screen is showing the timer</p>
          </div>
        )}

        {/* QUICK ACTIONS */}
        <div className="space-y-3 mb-6">
          <button
            onClick={resetReveal}
            disabled={phase === 'live'}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white/85 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="prom-control-reset"
          >
            <ArrowsClockwise size={14} weight="bold" /> Reset (return to live voting)
          </button>
          <button
            onClick={revealNow}
            disabled={phase === 'live' || phase === 'reveal'}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white/85 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="prom-control-reveal-now"
          >
            <Crown size={14} weight="fill" /> Reveal now (skip countdown)
          </button>
          <button
            onClick={openLive}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white border border-[#B89762]/40/25 bg-[#B89762]/30/8 hover:bg-[#B89762]/30/12 transition-colors"
            data-testid="prom-control-open-live"
          >
            <MonitorPlay size={14} weight="fill" /> Open live screen
          </button>
        </div>

        {/* PROJECTOR LINK */}
        <div className="rounded-xl bg-white/[0.03] border border-white/8 p-4" data-testid="prom-control-projector-link">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45 mb-2">Project this URL</p>
          <p className="text-[12px] text-white/85 break-all mb-3">{liveScreenUrl}</p>
          <button
            onClick={copyLiveLink}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-white hover:bg-neutral-200 transition-colors"
            data-testid="prom-control-copy-live-url"
          >
            <Copy size={12} weight="bold" /> Copy live screen URL
          </button>
        </div>

        {/* BOOST PARTICIPATION — small but high-impact teacher prompt */}
        <BoostParticipation slug={slug} />

        {/* POST-EVENT: Highlights generator — the next-day viral trigger */}
        <PromHighlightTrigger eventId={eventIdRef.current} slug={slug} />

        {/* Soft helper */}
        <button
          onClick={() => navigate(`/prom/${slug}/challenges`)}
          className="w-full text-center text-[12px] text-[#C29D52]/80 hover:text-[#C29D52]/85 mt-6 underline-offset-2 hover:underline flex items-center justify-center gap-1.5"
          data-testid="prom-control-view-challenges"
        >
          <Trophy size={12} weight="fill" /> View all challenges
        </button>
      </div>
    </div>
  );
}

function BoostParticipation({ slug }) {
  const guestUrl = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';
  const message = `Upload your prom photos here 🎉\n${guestUrl}`;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      trackGrowth('boost_message_copied', { metadata: { slug } });
      toast.success('Message copied — paste in your school WhatsApp / class group');
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  return (
    <div className="rounded-xl bg-amber-300/8 border border-amber-300/20 p-4 mt-4" data-testid="prom-control-boost">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
        <Megaphone size={11} weight="fill" /> Boost participation
      </p>
      <ul className="text-[12px] text-white/75 space-y-1 mb-3 list-disc pl-4">
        <li>Share the link in your school group chat</li>
        <li>Remind students mid-event ("photos for the highlight!")</li>
        <li>Call out a specific challenge ("best dance move on stage now")</li>
      </ul>
      <div className="rounded-lg bg-black/30 border border-white/8 p-3 mb-3">
        <p className="text-[11px] text-white/55 mb-1 font-black uppercase tracking-wider">One-tap message</p>
        <p className="text-[12px] text-white/85 whitespace-pre-line break-words">{message}</p>
      </div>
      <button
        onClick={copyMessage}
        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-amber-200 hover:bg-amber-300 transition-colors"
        data-testid="prom-control-copy-boost-message"
      >
        <ChatCircleText size={12} weight="fill" /> Copy share message
      </button>
    </div>
  );
}

function PromHighlightTrigger({ eventId, slug }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/highlight/public-status`);
        if (!cancelled) setStatus(r.data);
      } catch { /* ignore */ }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  const hlStatus = status?.status || 'none';

  const generate = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/events/${eventId}/prom-highlight/generate`, { trigger: 'host_panel' });
      toast.success('🎬 Generating prom highlights...');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start generation');
    } finally { setStarting(false); }
  };

  const openEvent = () => window.open(`/event/${slug}?focus=highlight`, '_blank', 'noopener');

  return (
    <div className="rounded-xl bg-[#B89762]/30/8 border border-[#B89762]/40/20 p-4 mt-4" data-testid="prom-control-highlight-trigger">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52]/85 mb-2 inline-flex items-center gap-1.5">
        <FilmReel size={11} weight="fill" /> Post-event highlight reel
      </p>
      {hlStatus === 'ready' ? (
        <>
          <p className="text-[12px] text-white/75 mb-3">Your 30-second recap is ready. Open the event page to share it.</p>
          <button
            onClick={openEvent}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-[#B89762]/20 hover:bg-[#B89762]/30 transition-colors"
            data-testid="prom-control-open-highlight"
          >
            <FilmReel size={12} weight="fill" /> Open highlight ({status?.duration || '30s'})
          </button>
          {status?.shares_count > 0 && (
            <p className="text-[11px] text-white/50 text-center mt-2" data-testid="prom-control-shares-count">
              Shared {status.shares_count} {status.shares_count === 1 ? 'time' : 'times'}
            </p>
          )}
        </>
      ) : hlStatus === 'processing' ? (
        <>
          <p className="text-[12px] text-white/75 mb-2">Cooking highlights... {status?.progress || 0}%</p>
          <div className="w-full h-1.5 bg-white/8 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-[#B89762]/30 transition-all duration-1000" style={{ width: `${status?.progress || 0}%` }} />
          </div>
        </>
      ) : (
        <>
          <p className="text-[12px] text-white/75 mb-3">After the prom, generate a 30-second recap students can share on WhatsApp.</p>
          <button
            onClick={generate}
            disabled={starting}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-[#B89762]/20 hover:bg-[#B89762]/30 transition-colors disabled:opacity-50"
            data-testid="prom-control-generate-highlight"
          >
            <FilmReel size={12} weight="fill" /> {starting ? 'Starting...' : 'Generate highlights now'}
          </button>
          <p className="text-[11px] text-white/50 text-center mt-2">Auto-creates 4h after the event if you forget</p>
        </>
      )}
    </div>
  );
}
